import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Check, X, AlertCircle, Trophy, Users as UsersIcon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { limitsFor } from "@/lib/plan";
import LockedCard from "@/components/LockedCard";

export default function Competitors() {
  const { id } = useParams();
  const { user } = useAuth();
  const { lang, t } = useLang();
  const limits = limitsFor(user);
  const canSeeCompetitors = !!limits.competitors;
  const [scan, setScan] = useState(null);
  const [biz, setBiz] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!canSeeCompetitors) {
      setLoading(false);
      return;
    }
    Promise.all([
      api.get(`/businesses/${id}`).then((r) => r.data),
      api.get(`/scans/${id}/latest`, { params: { lang } }).then((r) => r.data).catch(() => null),
    ]).then(([b, s]) => { setBiz(b); setScan(s); setLoading(false); });
  }, [id, canSeeCompetitors, lang]);

  if (!canSeeCompetitors) {
    return (
      <div className="space-y-6" data-testid="competitors-page">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-purple-400 mb-2">
            <UsersIcon size={12} /> {t("comp.eyebrow")}
          </div>
          <h1 className="font-display text-4xl font-bold text-white">{t("comp.title")}</h1>
        </div>
        <LockedCard
          title={t("comp.lockTitle")}
          description={t("comp.lockDesc")}
          requiredPlan="Growth"
          testid="competitors-locked"
        />
      </div>
    );
  }

  if (loading) return <div className="text-slate-400 flex items-center"><Loader2 className="animate-spin mr-2" /> {t("common.loading")}</div>;
  if (!scan) return <div className="text-slate-400">{t("comp.runAnalysisFirst")}</div>;

  const us = scan.fetched || {};
  const signals = scan.competitor_signals || [];
  const compScores = scan.competitor_scores || [];
  const cmp = scan.competitor_comparison || {};
  const reasons = Array.isArray(cmp.why_competitors_stronger) ? cmp.why_competitors_stronger : (cmp.why_competitors_stronger ? [cmp.why_competitors_stronger] : []);

  const rows = [
    { key: "has_about", label: t("comp.row.about") },
    { key: "has_services", label: t("comp.row.services") },
    { key: "has_faq", label: t("comp.row.faq") },
    { key: "has_contact", label: t("comp.row.contact") },
  ];

  return (
    <div className="space-y-6" data-testid="competitors-page">
      <div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-purple-400 mb-2">
          <UsersIcon size={12} /> {t("comp.eyebrow")}
        </div>
        <h1 className="font-display text-4xl font-bold text-white">{t("comp.title")}</h1>
        {cmp.summary && <p className="text-slate-400 mt-2 max-w-3xl">{cmp.summary}</p>}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <ScoreCard label={biz?.name || t("comp.competitorN.you")} score={scan.overall_score} isUs yourScoreLabel={t("comp.yourScore")} />
        {compScores.slice(0, 3).map((c, i) => (
          <ScoreCard key={i} label={t("comp.competitorN", { n: c.index ?? i + 1 })} sub={c.url} score={c.score} />
        ))}
        {Array.from({ length: Math.max(0, 3 - compScores.length) }).map((_, i) => (
          <div key={`empty-${i}`} className="glass rounded-2xl p-5 border-dashed">
            <div className="text-[10px] uppercase tracking-wider text-slate-600 mb-2">{t("comp.competitorN", { n: compScores.length + i + 1 })}</div>
            <div className="font-mono text-3xl text-slate-700">-</div>
            <div className="text-xs text-slate-600 mt-1">{t("comp.notAdded")}</div>
          </div>
        ))}
      </div>

      {signals.length > 0 && (
        <div className="glass-strong rounded-3xl overflow-hidden">
          <div className="px-7 py-4 border-b border-white/5">
            <div className="text-xs uppercase tracking-wider text-slate-400">{t("comp.signalsTable")}</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left bg-white/[0.02]">
                  <th className="px-5 py-3 text-xs uppercase tracking-wider text-slate-400 font-medium">{t("comp.eyebrow")}</th>
                  <th className="px-5 py-3 text-xs uppercase tracking-wider text-cyan-300 font-medium">{biz?.name} ({t("comp.competitorN.you")})</th>
                  {signals.map((c, i) => (
                    <th key={i} className="px-5 py-3 text-xs uppercase tracking-wider text-slate-400 font-medium max-w-[200px]">
                      {t("comp.competitorN", { n: i + 1 })}
                      <div className="text-[10px] text-slate-500 normal-case font-mono truncate">{c.title || c.url}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-5 py-3.5 text-white">{r.label}</td>
                    <td className="px-5 py-3.5"><Cell value={us[r.key]} highlight yes={t("comp.cell.yes")} no={t("comp.cell.no")} /></td>
                    {signals.map((c, i) => (
                      <td key={i} className="px-5 py-3.5"><Cell value={c[r.key]} yes={t("comp.cell.yes")} no={t("comp.cell.no")} /></td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-5">
        <div className="glass rounded-3xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle size={16} className="text-amber-400" />
            <h3 className="font-display font-semibold text-white">{t("comp.missingTitle")}</h3>
          </div>
          <ul className="space-y-2 text-sm text-slate-300">
            {(cmp.missing_signals || []).length === 0 && <li className="text-slate-500">{t("comp.noMissing")}</li>}
            {(cmp.missing_signals || []).map((s, i) => (
              <li key={i} className="flex gap-2"><X size={14} className="text-red-400 mt-0.5 shrink-0" />{s}</li>
            ))}
          </ul>
        </div>
        <div className="glass rounded-3xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <Trophy size={16} className="text-purple-400" />
            <h3 className="font-display font-semibold text-white">{t("comp.whyStronger")}</h3>
          </div>
          {reasons.length === 0 ? (
            <p className="text-sm text-slate-500">{t("comp.noStronger")}</p>
          ) : (
            <ul className="space-y-2 text-sm text-slate-300">
              {reasons.map((s, i) => (
                <li key={i} className="flex gap-2"><Trophy size={13} className="text-purple-400 mt-1 shrink-0" />{s}</li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {compScores.length > 0 && (
        <div className="space-y-3">
          <div className="text-xs uppercase tracking-wider text-slate-400 mt-2">{t("comp.competitorDetails")}</div>
          {compScores.map((c, i) => (
            <div key={i} className="glass-strong rounded-2xl p-5">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div className="min-w-0 flex-1">
                  <div className="font-display font-semibold text-white">{t("comp.competitorN", { n: c.index ?? i + 1 })}</div>
                  <a href={c.url} target="_blank" rel="noreferrer" className="text-xs text-slate-400 hover:text-cyan-300 truncate block">{c.url}</a>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">{t("common.score")}</div>
                  <div className="font-mono text-2xl text-white">{c.score}</div>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-amber-300 mb-2">{t("comp.detail.strongerThanYou")}</div>
                  <ul className="space-y-1 text-xs text-slate-300">
                    {(c.strengths || []).map((s, j) => <li key={j} className="flex gap-1.5"><Check size={11} className="text-amber-400 mt-0.5 shrink-0" />{s}</li>)}
                  </ul>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2">{t("comp.detail.alsoMissing")}</div>
                  <ul className="space-y-1 text-xs text-slate-400">
                    {(c.missing_on_them || []).map((s, j) => <li key={j} className="flex gap-1.5"><X size={11} className="text-slate-500 mt-0.5 shrink-0" />{s}</li>)}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ScoreCard({ label, sub, score, isUs, yourScoreLabel }) {
  const safe = score ?? 0;
  return (
    <div className={`glass rounded-2xl p-5 ${isUs ? "border-cyan-500/40" : ""}`}>
      <div className={`text-[10px] uppercase tracking-wider mb-2 ${isUs ? "text-cyan-300" : "text-slate-500"}`}>{isUs ? yourScoreLabel : label}</div>
      {sub && !isUs && <div className="text-xs text-slate-500 truncate mb-1">{sub}</div>}
      {isUs && <div className="text-sm text-white font-medium truncate mb-1">{label}</div>}
      <div className={`font-mono text-3xl ${isUs ? "gradient-text" : "text-white"}`}>{safe}</div>
    </div>
  );
}

function Cell({ value, highlight, yes, no }) {
  if (value === true) return <span className={`inline-flex items-center gap-1 text-xs ${highlight ? "text-cyan-300" : "text-emerald-300"}`}><Check size={12} /> {yes}</span>;
  if (value === false) return <span className="inline-flex items-center gap-1 text-xs text-red-300"><X size={12} /> {no}</span>;
  return <span className="text-xs text-slate-500">-</span>;
}
